import { useRef, useCallback, useState } from 'react';
import { useCanvasStore } from '../../stores/canvasStore';
import { useStudioStore } from '../../stores/studioStore';
import { useViewportStore } from '../../stores/viewportStore';
import { useClipboardStore } from '../../stores/clipboardStore';
import { useAssetStore } from '../../stores/assetStore';
import GridOverlay from './GridOverlay';
import SmartGuides from './SmartGuides';
import PathPointEditor from './PathPointEditor';
import RulerOverlay from './RulerOverlay';
import GuideLinesOverlay from './GuideLinesOverlay';
import EyedropperPreview from './EyedropperPreview';
import { CanvasContextMenu } from './CanvasContextMenu';
import { SvgImportDialog } from './SvgImportDialog';
import FontMatchDialog from './FontMatchDialog';
import { ASSET_DRAG_MIME } from './asset/AssetTile';
import { buildElementFromAsset, resolveAssetById } from '../../lib/assetInsert';
import { analyzeSvgForSmartMerge } from '../../lib/svgImport';
import { ARTBOARD_PADDING } from '../../lib/textMeasurement';
import { useCanvasInit } from '../../hooks/useCanvasInit';
import { useCanvasSync } from '../../hooks/useCanvasSync';
import { useCanvasEvents } from '../../hooks/useCanvasEvents';
import { useSvgImportFlow } from '../../hooks/useSvgImportFlow';
import { useImageUpload } from '../../hooks/useImageUpload';
import type { CanvasElement, PathElement } from '../../types';

export default function CanvasWorkspace() {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const {
    fabricCanvas,
    artboardRef,
    containerWidth,
    containerHeight,
    lineDrawRef,
    penStateRef,
    penPreviewPathRef,
    penPreviewDotsRef,
    drawStateRef,
  } = useCanvasInit({
    containerRef,
    canvasElRef: canvasRef,
  });

  useCanvasSync({ fabricCanvas, artboardRef });

  const { fileInputRef, imageClickPosRef, handleImageUpload } = useImageUpload({
    artboardRef,
  });

  const { editingPathId, setEditingPathId, contextMenu, setContextMenu } = useCanvasEvents({
    fabricCanvas,
    artboardRef,
    lineDrawRef,
    penStateRef,
    penPreviewPathRef,
    penPreviewDotsRef,
    drawStateRef,
    imageClickPosRef,
    fileInputRef,
  });

  const {
    isImporting,
    importStatus,
    svgImportPending,
    setSvgImportPending,
    fontMatchPending,
    handleFontMatchConfirm,
    handleFontMatchCancel,
    handleSvgImportDecomposed,
    handleSvgImportGrouped,
    handleSvgImportSmartMerge,
  } = useSvgImportFlow();

  const [isDragOver, setIsDragOver] = useState(false);

  const { activeTool, canvasWidth, canvasHeight } = useCanvasStore();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const types = Array.from(e.dataTransfer.types || []);
    const hasAsset = types.includes(ASSET_DRAG_MIME);
    const hasFiles = types.includes('Files');
    if (hasAsset) {
      e.dataTransfer.dropEffect = 'copy';
      setIsDragOver(true);
    } else if (hasFiles) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const assetId = e.dataTransfer.getData(ASSET_DRAG_MIME);
    if (assetId) {
      const asset = await resolveAssetById(assetId);
      if (!asset) return;

      const canvas = useCanvasStore.getState().fabricCanvas;
      const artboard = artboardRef.current;
      const cw = useCanvasStore.getState().canvasWidth;
      const ch = useCanvasStore.getState().canvasHeight;

      let position: { x: number; y: number } | undefined;
      if (canvas && artboard && e.nativeEvent instanceof MouseEvent) {
        try {
          const pointer = canvas.getPointer(e.nativeEvent);
          const artboardLeft = artboard.left ?? 0;
          const artboardTop = artboard.top ?? 0;
          position = {
            x: Math.round(pointer.x - artboardLeft),
            y: Math.round(pointer.y - artboardTop),
          };
        } catch {
          // fall back to centered placement
        }
      }

      const element = await buildElementFromAsset(asset, {
        canvasWidth: cw,
        canvasHeight: ch,
        elementsCount: useStudioStore.getState().canvasState.elements.length,
        position,
      });
      if (element) {
        useStudioStore.getState().addElement(element);
        useAssetStore.getState().incrementUsage(asset.id);
      }
      return;
    }

    const svgFiles = Array.from(e.dataTransfer.files).filter(
      (f) => f.name.toLowerCase().endsWith('.svg') || f.type === 'image/svg+xml'
    );
    if (svgFiles.length > 0) {
      await handleSvgImportSmartMerge(svgFiles);
    }
  }, [handleSvgImportSmartMerge, artboardRef]);

  const vpState = useViewportStore();

  const screenArtboardLeft = ARTBOARD_PADDING * vpState.zoom + vpState.panX;
  const screenArtboardTop = ARTBOARD_PADDING * vpState.zoom + vpState.panY;

  return (
    <div
      ref={containerRef}
      className={`flex-1 relative overflow-hidden bg-[#0a0a0a] ${isDragOver ? 'ring-2 ring-cyan-500 ring-inset' : ''}`}
      style={{ cursor: activeTool === 'pan' ? 'grab' : undefined }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <GridOverlay
        containerWidth={containerWidth}
        containerHeight={containerHeight}
        artboardWidth={canvasWidth}
        artboardHeight={canvasHeight}
      />

      <canvas ref={canvasRef} />

      <SmartGuides
        containerWidth={containerWidth}
        containerHeight={containerHeight}
      />

      <GuideLinesOverlay
        artboardLeft={screenArtboardLeft}
        artboardTop={screenArtboardTop}
        containerWidth={containerWidth}
        containerHeight={containerHeight}
      />

      {editingPathId && (() => {
        const pathElement = useStudioStore.getState().canvasState.elements.find(
          (el) => el.id === editingPathId && el.type === 'path'
        ) as PathElement | undefined;
        if (!pathElement || !artboardRef.current) return null;
        return (
          <PathPointEditor
            element={pathElement}
            artboardLeft={artboardRef.current.left ?? ARTBOARD_PADDING}
            artboardTop={artboardRef.current.top ?? ARTBOARD_PADDING}
            zoom={vpState.zoom}
            panX={vpState.panX}
            panY={vpState.panY}
            onExitEdit={() => setEditingPathId(null)}
          />
        );
      })()}

      <EyedropperPreview containerRef={containerRef} />

      <RulerOverlay
        artboardLeft={screenArtboardLeft}
        artboardTop={screenArtboardTop}
        containerWidth={containerWidth}
        containerHeight={containerHeight}
        onGuideCreate={(orientation, position) => {
          useViewportStore.getState().addGuide(orientation, position);
        }}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={handleImageUpload}
      />

      {isDragOver && (
        <div className="absolute inset-0 bg-cyan-500/10 border-2 border-dashed border-cyan-500/50 rounded-lg z-50 flex items-center justify-center pointer-events-none">
          <div className="bg-zinc-900/90 px-6 py-3 rounded-xl border border-cyan-500/30">
            <p className="text-cyan-300 font-medium text-sm">Drop SVG files to import</p>
          </div>
        </div>
      )}

      {isImporting && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 p-8 rounded-xl bg-white/5 border border-white/10">
            <div className="relative w-12 h-12">
              <div className="absolute inset-0 rounded-full border-2 border-white/10" />
              <div className="absolute inset-0 rounded-full border-2 border-t-cyan-400 animate-spin" />
            </div>
            <div className="text-sm text-white/70 font-medium">
              {importStatus || 'Importing...'}
            </div>
          </div>
        </div>
      )}

      {svgImportPending && (
        <SvgImportDialog
          fileName={svgImportPending.file.name}
          elementCount={svgImportPending.elementCount}
          onImportDecomposed={async () => {
            setSvgImportPending(null);
            await handleSvgImportDecomposed([svgImportPending.file]);
          }}
          onImportGrouped={async () => {
            setSvgImportPending(null);
            await handleSvgImportGrouped([svgImportPending.file]);
          }}
          onImportSmartMerge={async () => {
            setSvgImportPending(null);
            await handleSvgImportSmartMerge([svgImportPending.file]);
          }}
          analyzeSmartMerge={async () => {
            return analyzeSvgForSmartMerge(svgImportPending.file);
          }}
          onCancel={() => setSvgImportPending(null)}
        />
      )}

      {fontMatchPending && (
        <FontMatchDialog
          matchResults={fontMatchPending.matchResults}
          onConfirm={handleFontMatchConfirm}
          onCancel={handleFontMatchCancel}
        />
      )}

      {contextMenu && <ContextMenuWrapper
        x={contextMenu.x}
        y={contextMenu.y}
        onClose={() => setContextMenu(null)}
      />}
    </div>
  );
}

function ContextMenuWrapper({ x, y, onClose }: { x: number; y: number; onClose: () => void }) {
  const { selectedElementIds, canvasState, removeElement, clearSelection, reorderElement, duplicateElement, updateElement, updateElements, groupSelected, ungroupSelected, mergeSelectedToSvg, explodeSvgElement } = useStudioStore();
  const clipboard = useClipboardStore();

  const selectedElements = canvasState.elements.filter((el) => selectedElementIds.includes(el.id));
  const count = selectedElements.length;

  const allLocked = count > 0 && selectedElements.every((el) => el.locked);
  const allVisible = count > 0 && selectedElements.every((el) => el.visible);

  const canGroup = count >= 2;
  const canUngroup = count === 1 && selectedElements[0]?.type === 'group';
  const canMerge = count >= 2;
  const canExplode = count === 1 && selectedElements[0]?.type === 'svg';

  const handleCopy = () => {
    if (selectedElements.length > 0) {
      clipboard.copy(selectedElements, canvasState.elements);
    }
  };

  const handleCut = () => {
    if (selectedElements.length > 0) {
      clipboard.copy(selectedElements, canvasState.elements);
      const ids = [...selectedElementIds];
      for (const id of ids) removeElement(id);
      clearSelection();
    }
  };

  const handlePaste = () => {
    const newElements = clipboard.paste();
    if (newElements) {
      const newIds: string[] = [];
      for (const el of newElements) {
        useStudioStore.getState().addElement(el as CanvasElement);
        newIds.push((el as CanvasElement).id);
      }
      useStudioStore.getState().setSelectedElements(newIds);
    }
  };

  const handleDuplicate = () => {
    if (count === 1) {
      duplicateElement(selectedElementIds[0]);
    } else if (count > 1) {
      clipboard.copy(selectedElements, canvasState.elements);
      const newElements = clipboard.paste();
      if (newElements) {
        const newIds: string[] = [];
        for (const el of newElements) {
          useStudioStore.getState().addElement(el as CanvasElement);
          newIds.push((el as CanvasElement).id);
        }
        useStudioStore.getState().setSelectedElements(newIds);
      }
    }
  };

  const handleDelete = () => {
    const ids = [...selectedElementIds];
    for (const id of ids) removeElement(id);
    clearSelection();
  };

  const handleReorder = (direction: 'up' | 'down' | 'top' | 'bottom') => {
    for (const id of selectedElementIds) {
      reorderElement(id, direction);
    }
  };

  const handleToggleLock = () => {
    const newLocked = !allLocked;
    updateElements(selectedElements.map((el) => ({ id: el.id, changes: { locked: newLocked } })));
  };

  const handleToggleVisibility = () => {
    const newVisible = !allVisible;
    updateElements(selectedElements.map((el) => ({ id: el.id, changes: { visible: newVisible } })));
  };

  return (
    <CanvasContextMenu
      x={x}
      y={y}
      onClose={onClose}
      selectedCount={count}
      onBringToFront={() => handleReorder('top')}
      onBringForward={() => handleReorder('up')}
      onSendBackward={() => handleReorder('down')}
      onSendToBack={() => handleReorder('bottom')}
      onDuplicate={handleDuplicate}
      onDelete={handleDelete}
      onCopy={handleCopy}
      onCut={handleCut}
      onPaste={handlePaste}
      onToggleLock={handleToggleLock}
      onToggleVisibility={handleToggleVisibility}
      onGroup={groupSelected}
      onUngroup={ungroupSelected}
      onMergeToSvg={mergeSelectedToSvg}
      onExplode={() => explodeSvgElement()}
      isLocked={allLocked}
      isVisible={allVisible}
      canPaste={clipboard.hasContent()}
      canGroup={canGroup}
      canUngroup={canUngroup}
      canMerge={canMerge}
      canExplode={canExplode}
    />
  );
}
